import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { promotionsAPI } from '../services/api';
import { LoadingScreen, ErrorScreen } from '../hooks/useApiLoading';
import { useNotificationHook } from '../hooks/useNotificationHook';
import { 
  Code, Smartphone, TrendingUp, Globe, Search, Share2, ShoppingCart, Palette, Database, Cloud, Shield, Zap, CheckCircle, ArrowRight, Star, Users, Clock, Award, MessageCircle, BarChart3, Target, Rocket, Loader2, AlertCircle, Activity, Settings, Eye, Home, Calendar, DollarSign, Plus, RefreshCw, ChevronLeft, ChevronRight, Video, FileText, Tag
} from 'lucide-react';
import { gsap } from 'gsap';
import WishlistButton from '../components/WishlistButton';
import SharePrintButtons from '../components/SharePrintButtons';

// Add CSS for scrolling animation
const scrollAnimation = `
  @keyframes scroll {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
`;

const ICONS = {
  Code, Smartphone, TrendingUp, Globe, ShoppingCart, Palette, Database, Cloud, Shield, Zap, CheckCircle, ArrowRight, Star, Users, Clock, Award, MessageCircle, BarChart3, Target, Rocket, Loader2, AlertCircle, Activity, Settings, Eye, Home, Calendar, DollarSign, Plus, RefreshCw
};

const Services = () => {
  // --- State for all sections ---
  const [main, setMain] = useState({ main_title: '', gradient_title: '', description: '' });
  const [stats, setStats] = useState([]);
  const [additional, setAdditional] = useState({ additional_title: '', additional_description: '' });
  const [process, setProcess] = useState({ process_title: '', process_description: '' });
  const [steps, setSteps] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialSection, setTestimonialSection] = useState({ testimonial_title: '', testimonial_description: '' });
  const [cta, setCta] = useState({ cta_title: '', cta_description: '', cta_button1_text: '', cta_button1_link: '', cta_button2_text: '', cta_button2_link: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [servicesError, setServicesError] = useState(null);
  const statsRefs = useRef([]);
  const [statsAnimated, setStatsAnimated] = useState(false);
  
  // Banner promotions state
  const [promotions, setPromotions] = useState([]);
  const [topBannerPromotions, setTopBannerPromotions] = useState([]);
  const [topBannerIndex, setTopBannerIndex] = useState(0);

  const { notify } = useNotificationHook();

  // Add CSS animation for scrolling testimonials
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = scrollAnimation;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // --- Fetch all Services Page content ---
  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/services-page/main'),
      api.get('/services-page/stats'),
      api.get('/services-page/additional'),
      api.get('/services-page/process'),
      api.get('/services-page/testimonials'),
      api.get('/services-page/cta')
    ]).then(([mainRes, statsRes, additionalRes, processRes, testimonialsRes, ctaRes]) => {
      setMain(mainRes.data.data || {});
      setStats(statsRes.data.data || []);
      setAdditional({
        additional_title: additionalRes.data.data.additional_title || '',
        additional_description: additionalRes.data.data.additional_description || '',
        additional_services: additionalRes.data.data.additional_services ? 
          (typeof additionalRes.data.data.additional_services === 'string' ? 
            JSON.parse(additionalRes.data.data.additional_services) : 
            additionalRes.data.data.additional_services) : []
      });
      setProcess({ 
        process_title: processRes.data.data.process_title || '', 
        process_description: processRes.data.data.process_description || '',
        process_steps: Array.isArray(processRes.data.data.steps) ? processRes.data.data.steps : []
      });
      setSteps(processRes.data.data.steps || []);
      setTestimonialSection({ testimonial_title: testimonialsRes.data.data.testimonial_title, testimonial_description: testimonialsRes.data.data.testimonial_description });
      setTestimonials(testimonialsRes.data.data.testimonials || []);
      setCta(ctaRes.data.data || {});
      setLoading(false);
    }).catch(err => {
      setError('Failed to load Services page content');
      notify.error('Failed to load Services page content', 'Loading Error');
      setLoading(false);
    });
  }, []);

  // --- Fetch promotions for services page ---
  useEffect(() => {
    const fetchPromotions = async () => {
      try {
        const response = await promotionsAPI.getAll({
          page: 'services'
        });
        
        if (response?.data?.success) {
          const activePromotions = response.data.data.promotions?.filter(promotion => {
            // Check if promotion is active
            if (!promotion.is_active) return false;
            
            // Check if promotion is for services page
            if (!promotion.show_on_pages?.includes('services')) return false;
            
            // Allow all promotion types for services page
            // (Top banner will filter to only banner/coupon, bottom section will show all types)
            
            // Check validity dates
            const now = new Date();
            if (promotion.valid_from && new Date(promotion.valid_from) > now) return false;
            if (promotion.valid_until && new Date(promotion.valid_until) < now) return false;
            
            return true;
          }) || [];
          
          setPromotions(activePromotions);
          
          // Filter promotions for top banner (only banner and coupon types)
          const topBannerPromos = activePromotions.filter(promotion => 
            ['banner', 'coupon'].includes(promotion.type)
          );
          setTopBannerPromotions(topBannerPromos);
        }
      } catch (err) {
        console.error('Error fetching promotions:', err);
      }
    };

    fetchPromotions();
  }, []);

  // Auto-scroll for top banner promotions
  useEffect(() => {
    if (topBannerPromotions.length <= 1) return;

    const topBannerInterval = setInterval(() => {
      setTopBannerIndex(prev => (prev + 1) % topBannerPromotions.length);
    }, 5000); // Change every 5 seconds

    return () => {
      clearInterval(topBannerInterval);
    };
  }, [topBannerPromotions.length]);

  // --- Fetch main services (already dynamic) ---
  useEffect(() => {
    setServicesLoading(true);
    setServicesError(null);
    api.get('/services', { params: { status: 'active', featured: 'true' } })
      .then(response => {
        if (response?.data?.success) {
          const servicesData = response.data.data.services || [];
          const formattedServices = servicesData.map(service => {
            try {
              return {
                ...service,
                features: typeof service.features === 'string' ? JSON.parse(service.features) : service.features || [],
                featured: Boolean(service.featured),
                iconComponent: ICONS[service.icon] || Globe,
                formattedPrice: `₹${parseInt(service.price).toLocaleString()}`,
                color: getCategoryColor(service.category),
                bgColor: getCategoryBgColor(service.category)
              };
            } catch (parseError) {
              return {
                ...service,
                features: [],
                featured: Boolean(service.featured),
                iconComponent: Globe,
                formattedPrice: `₹${parseInt(service.price).toLocaleString()}`,
      color: 'from-blue-600 to-blue-700',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20'
              };
            }
          });
          setServices(formattedServices);
        } else {
          setServicesError('Failed to fetch services');
        }
      })
      .catch(err => setServicesError('Error loading services: ' + err.message))
      .finally(() => setServicesLoading(false));
  }, []);

  // --- Count-up animation for stats ---
  useEffect(() => {
    if (!stats.length || statsAnimated) return;
    const handleScroll = () => {
      const section = document.getElementById('services-stats-section');
      if (section && window.scrollY + window.innerHeight > section.offsetTop + 50) {
        setStatsAnimated(true);
        stats.forEach((stat, i) => {
          if (statsRefs.current[i]) {
            gsap.fromTo(statsRefs.current[i], { innerText: 0 }, {
              innerText: parseInt(stat.value.replace(/\D/g, '')) || 0,
              duration: 1.5,
              snap: { innerText: 1 },
              onUpdate: function () {
                statsRefs.current[i].innerText = Math.floor(this.targets()[0].innerText) + (stat.value.match(/\D+/g) ? stat.value.match(/\D+/g)[0] : '');
              }
            });
          }
        });
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [stats, statsAnimated]);

  // --- Helper functions ---
  const getCategoryColor = (category) => {
    const colors = {
      'Development': 'from-blue-600 to-blue-700',
      'Marketing': 'from-green-600 to-green-700',
      'Design': 'from-purple-600 to-purple-700',
      'E-commerce': 'from-pink-600 to-pink-700',
      'Support': 'from-orange-600 to-orange-700'
    };
    return colors[category] || 'from-blue-600 to-blue-700';
  };
  const getCategoryBgColor = (category) => {
    const bgColors = {
      'Development': 'bg-blue-50 dark:bg-blue-900/20',
      'Marketing': 'bg-green-50 dark:bg-green-900/20',
      'Design': 'bg-purple-50 dark:bg-purple-900/20',
      'E-commerce': 'bg-pink-50 dark:bg-pink-900/20',
      'Support': 'bg-orange-50 dark:bg-orange-900/20'
    };
    return bgColors[category] || 'bg-blue-50 dark:bg-blue-900/20';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading Services Page...</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <AlertCircle className="w-12 h-12 text-red-600 mb-4" />
        <p className="text-lg text-red-600 mb-2">{error}</p>
      </div>
    );
  }

  // Show loading screen when data is being fetched
  if (loading) {
    return <LoadingScreen message="Loading services..." />;
  }

  // Show error screen when there's an error
  if (error) {
    return <ErrorScreen error={error} onRetry={() => window.location.reload()} message="Unable to Load Services" />;
  }

  // Don't render content if no data is available
  if (!main || !main.main_title) {
    return <LoadingScreen message="Loading content..." />;
  }

  return (
    <div className="min-h-screen">
      {/* Modern Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-100/10 via-purple-100/10 to-pink-100/10 dark:from-blue-800/5 dark:via-purple-800/5 dark:to-pink-800/5"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="services-title text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              {main.main_title} <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">{main.gradient_title}</span>
            </h1>
            
            <p className="services-subtitle text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
              {main.description}
            </p>
            
            {/* Stats Section - Moved below description */}
            <div id="services-stats-section" className="flex items-center justify-center gap-8 text-sm text-gray-600 dark:text-gray-400">
              {stats.map((stat, i) => {
                const IconComponent = ICONS[stat.icon] || Users;
                return (
                  <div key={stat.id} className="flex items-center gap-2">
                    <IconComponent className="w-4 h-4" />
                    <span ref={el => statsRefs.current[i] = el}>{stat.value}</span>
                    <span>{stat.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Banner Promotions */}
      {topBannerPromotions.length > 0 && (
        <div className="bg-gradient-to-r from-blue-600 to-purple-600">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="relative">
              {/* Current Promotion */}
              {topBannerPromotions[topBannerIndex] && (
                <div 
                  key={topBannerPromotions[topBannerIndex].id} 
                  className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 transition-all duration-500 ease-in-out"
                >
                  <div className="flex items-center gap-4">
                    {/* Standardized Image/Video Space */}
                    <div className="flex-shrink-0 w-16 h-16">
                      {/* Banner Image */}
                      {topBannerPromotions[topBannerIndex].type === 'banner' && topBannerPromotions[topBannerIndex].image_url && (
                        <img 
                          src={topBannerPromotions[topBannerIndex].image_url} 
                          alt={topBannerPromotions[topBannerIndex].title}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      )}
                      
                      {/* Coupon Code Thumbnail */}
                      {topBannerPromotions[topBannerIndex].type === 'coupon' && (
                        <div className="w-full h-full bg-green-600 rounded-lg flex items-center justify-center p-2">
                          <div className="text-center">
                            <div className="text-white font-mono font-bold text-sm">
                              {topBannerPromotions[topBannerIndex].coupon_code}
                            </div>
                            {topBannerPromotions[topBannerIndex].discount_percentage > 0 && (
                              <div className="text-white/90 text-xs mt-1">
                                {topBannerPromotions[topBannerIndex].discount_percentage}% OFF
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      {/* Standardized Title */}
                      <h3 className="text-white font-semibold text-lg mb-1">
                        {topBannerPromotions[topBannerIndex].title}
                      </h3>
                      
                      {/* Standardized Description */}
                      {topBannerPromotions[topBannerIndex].description && (
                        <p className="text-white/90 text-sm mb-2">
                          {topBannerPromotions[topBannerIndex].description}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex-shrink-0">
                      <Tag className="w-5 h-5 text-white/70" />
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Controls */}
              {topBannerPromotions.length > 1 && (
                <div className="absolute top-1/2 transform -translate-y-1/2 flex justify-between w-full px-4">
                  <button
                    onClick={() => setTopBannerIndex(prev => prev > 0 ? prev - 1 : topBannerPromotions.length - 1)}
                    className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setTopBannerIndex(prev => (prev + 1) % topBannerPromotions.length)}
                    className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}

              {/* Dots Indicator */}
              {topBannerPromotions.length > 1 && (
                <div className="flex justify-center mt-4 space-x-2">
                  {topBannerPromotions.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setTopBannerIndex(index)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === topBannerIndex ? 'bg-white' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modern Main Services Section */}
      <section className="py-24 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-blue-900 dark:to-indigo-900 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-100/10 via-purple-100/10 to-pink-100/10 dark:from-blue-800/5 dark:via-purple-800/5 dark:to-pink-800/5"></div>
        </div>
        
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          {servicesLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="flex items-center gap-3">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                <span className="text-gray-600 dark:text-gray-400">Loading services...</span>
              </div>
            </div>
          ) : servicesError ? (
            <div className="text-center py-20">
              <div className="text-red-600 dark:text-red-400 mb-4">
                <AlertCircle className="w-12 h-12 mx-auto mb-4" />
                <p className="text-lg font-medium">{servicesError}</p>
                <button 
                  onClick={() => window.location.reload()}
                  className="mt-4 px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl hover:scale-105 transition-all duration-300"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : services.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-600 dark:text-gray-400">No services available at the moment.</p>
            </div>
          ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {services.map((service, index) => {
                const IconComponent = service.iconComponent;
              return (
                  <div key={service.id} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 hover:scale-105 transition-all duration-500 border border-white/20">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                        {service.description}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Features:</h4>
                      <ul className="space-y-2">
                          {service.features.slice(0, 4).map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Details:</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Starting from:</span>
                            <span className="font-bold text-blue-600 dark:text-blue-400">{service.formattedPrice}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Timeline:</span>
                          <span className="font-medium text-gray-900 dark:text-white">{service.duration}</span>
                        </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Category:</span>
                            <span className="font-medium text-gray-900 dark:text-white">{service.category}</span>
                          </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Link 
                      to="/quote" 
                      className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:scale-105 text-white font-semibold py-3 px-6 rounded-2xl transition-all duration-300 transform flex items-center justify-center gap-2 shadow-lg"
                    >
                      Get Quote
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link 
                      to="/contact" 
                      className="px-6 py-3 border-2 border-gray-900 dark:border-white text-gray-900 dark:text-white font-semibold rounded-2xl hover:bg-gray-900 hover:text-white dark:hover:bg-white dark:hover:text-gray-900 transition-all duration-300"
                    >
                      Learn More
                    </Link>
                  </div>
                  
                  {/* Wishlist and Share Buttons */}
                  <div className="flex justify-center mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-4">
                      <WishlistButton 
                        item={service} 
                        itemType="service" 
                        size="small"
                        showLabel={false}
                      />
                      <SharePrintButtons 
                        item={service} 
                        itemType="service" 
                        size="small"
                        showLabels={false}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </div>
      </section>

      {/* Modern Additional Services Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-purple-800 to-slate-800 opacity-20"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8">
              {additional.additional_title}
            </h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              {additional.additional_description}
            </p>
          </div>
          
          {/* Additional Services Grid */}
          {Array.isArray(additional.additional_services) && additional.additional_services.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {additional.additional_services.map((service, index) => {
                const IconComponent = ICONS[service.icon] || Globe;
              return (
                  <div key={index} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 hover:scale-105 transition-all duration-500 border border-white/20">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      {service.description}
                    </p>
                    
                    {/* Wishlist and Share Buttons */}
                    <div className="flex justify-center mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-4">
                        <WishlistButton 
                          item={service} 
                          itemType="service" 
                          size="small"
                          showLabel={false}
                        />
                        <SharePrintButtons 
                          item={service} 
                          itemType="service" 
                          size="small"
                          showLabels={false}
                        />
                      </div>
                    </div>
                  </div>
              );
            })}
          </div>
          )}
        </div>
      </section>

      {/* Modern Process Section */}
      <section className="py-24 bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-blue-900 dark:to-indigo-900 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-100/10 via-purple-100/10 to-pink-100/10 dark:from-blue-800/5 dark:via-purple-800/5 dark:to-pink-800/5"></div>
        </div>
        
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-8">
              {process.process_title}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {process.process_description}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.isArray(process.process_steps) && process.process_steps.map((step, index) => {
              const IconComponent = ICONS[step.icon] || CheckCircle;
              return (
                <div key={index} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 text-center hover:scale-105 transition-all duration-500 border border-white/20">
                  <div className="relative mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-xs font-bold text-white">{index + 1}</span>
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Modern Testimonials Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-purple-800 to-slate-800 opacity-20"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8">
              {testimonialSection.testimonial_title}
            </h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              {testimonialSection.testimonial_description}
            </p>
          </div>
          
          {/* Scrolling Carousel */}
          <div className="relative overflow-hidden">
            <div className="flex animate-scroll gap-8" style={{ animation: 'scroll 30s linear infinite' }}>
              {/* Duplicate testimonials for seamless scrolling */}
              {[...testimonials, ...testimonials].map((testimonial, index) => (
                <div key={index} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 min-w-[350px] flex-shrink-0 hover:scale-105 transition-all duration-500 border border-white/20">
                <div className="flex mb-4">
                    {[...Array(testimonial.rating || 5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-4 italic">
                    "{testimonial.testimonial}"
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      {testimonial.name}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        {testimonial.position} @ {testimonial.company}
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs rounded-full">
                      {testimonial.project_name}
                  </span>
                </div>
              </div>
            ))}
            </div>
          </div>
        </div>
      </section>

      {/* Modern CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-50 via-purple-50 to-indigo-50 dark:from-blue-600 dark:via-purple-600 dark:to-indigo-600 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-100/20 via-purple-100/20 to-indigo-100/20 dark:from-blue-700/20 dark:via-purple-700/20 dark:to-indigo-700/20"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
            {cta.cta_title}
          </h2>
          <p className="text-xl text-gray-600 dark:text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed">
            {cta.cta_description}
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link 
              to={cta.cta_button1_link || '/quote'} 
              className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-10 py-5 rounded-2xl font-bold text-lg hover:scale-105 transition-all duration-500 flex items-center justify-center gap-3 shadow-glow"
            >
              {cta.cta_button1_text}
              <ArrowRight className="w-6 h-6" />
            </Link>
            <Link 
              to={cta.cta_button2_link || '/contact'} 
              className="card-3d border-2 border-gray-900 dark:border-white text-gray-900 dark:text-white px-10 py-5 rounded-2xl font-bold text-lg hover:bg-gray-900 hover:text-white dark:hover:bg-white dark:hover:text-gray-900 hover:scale-105 transition-all duration-500 shadow-glow"
            >
              {cta.cta_button2_text}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
